import React, { Suspense, useMemo } from "react";
import { Form, Col } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Await, useRouteLoaderData } from "react-router-dom";

export default function MarketPicker({ formData, setFormData, name, filterFunction = () => true, disabled, loadOnChange, colXl = 3 }) {
    const { radios, competitorRadios, competitorMarkets, airplayMarkets, radioTags } = useRouteLoaderData("root");

    const allPromise = useMemo(
        () => Promise.all([radios, competitorRadios, competitorMarkets, airplayMarkets, radioTags]),
        [airplayMarkets, competitorMarkets, competitorRadios, radioTags, radios]
    );

    async function handleChange(value) {
        let formDataCopy = { ...formData, market: value };
        if (loadOnChange && value.length) {
            const savedFormData = await loadOnChange(value[0].market_id);
            if (savedFormData) {
                formDataCopy = { ...formData, ...savedFormData, market: value };
            }
        }
        setFormData(formDataCopy);
    }

    return (
        <Suspense fallback={<Fallback name={name} colXl={colXl} />}>
            <Await resolve={allPromise}>
                {([resolvedRadios, resolvedCompetitorRadios, resolvedCompetitorsMarkets, resolvedAirplayMarkets, resolvedRadioTags]) => {
                    const combinedRadios = resolvedRadios.data.concat(
                        resolvedCompetitorRadios.data.filter(x => !resolvedRadios.data.map(rr => rr.radio_id).includes(x.radio_id))
                    );
                    const competitorsData = resolvedCompetitorsMarkets.data.map(competitorMarket => {
                        return {
                            market_id: competitorMarket.competitor_market_id,
                            radio_id: competitorMarket.radio.radio_id,
                            market_name: "Competitors - " + (competitorMarket.radio.radio_name ?? "Unknown"),
                            type: "competitor",
                        };
                    });
                    const airplayData = resolvedAirplayMarkets.data.map(airplayMarket => {
                        const radio = combinedRadios.find(radio => radio.radio_id === airplayMarket.radio_id);
                        return {
                            market_id: airplayMarket.radio_id,
                            market_name: "Airplay - " + (radio ? radio.radio_name : "Unknown"),
                            type: "airplay",
                        };
                    });
                    const radioTagsData = resolvedRadioTags.data.map(radioTag => {
                        return {
                            market_id: radioTag.id,
                            market_name: radioTag.name,
                            type: "tag",
                        };
                    });
                    const allMarkets = [...competitorsData, ...airplayData, ...radioTagsData].filter(filterFunction);

                    return (
                        <Form.Group as={Col} className={`col-12 col-xl-${colXl} min-w-station`}>
                            <Form.Label>Market</Form.Label>
                            <Typeahead
                                id={`${name}-market-selection`}
                                options={allMarkets}
                                labelKey={"market_name"}
                                highlightOnlyResult
                                placeholder="Select market..."
                                clearButton={true}
                                onChange={handleChange}
                                selected={formData.market}
                                disabled={disabled || false}
                            />
                        </Form.Group>
                    );
                }}
            </Await>
        </Suspense>
    );
}

function Fallback({ name, colXl }) {
    return (
        <Form.Group as={Col} className={`col-12 col-xl-${colXl} min-w-station`}>
            <Form.Label>Market</Form.Label>
            <Typeahead id={`${name}-market-selection-fallback`} options={[]} placeholder="Loading markets..." disabled />
        </Form.Group>
    );
}
